import { Injectable, Injector, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, of } from 'rxjs';
import { Router, Route } from '@angular/router';
import { RequireAuthentication } from './decorators/security.decorator';
import {
  AuthGuardService,
  SecurityRoles,
  SecurityGroups,
  SecurityAuthenticate
} from './services/auth-guard.service';

/**
 * This service is used to initialize any security models
 */
@Injectable()
export class AppLoadService {
  protected _router: Router | null = null;

  constructor(
    protected httpClient: HttpClient,
    protected injector: Injector,
    protected zone: NgZone
  ) {}

  /**
   * Executes prior to initialize and postInitialize
   */
  public async preInitialize(): Promise<any> {
    return await lastValueFrom(of(false));
  }

  /**
   * Executes after preInitialize but before postInitialize
   */
  public async initialize(): Promise<any> {
    return await lastValueFrom(of(false));
  }

  /**
   * Executes after both preInitialize and initialize
   */
  public async postInitialize(): Promise<any> {
    for (let route of this.router.config) {
      this.protectRoutes(route);
    }

    return await lastValueFrom(of(false));
  }

  /**
   * Adds a route guard to the component if it has been annotated with one of the security decorators
   * @param route The route to protect
   */
  private protectRoutes(route: Route): void {
    const component: any = route.component;

    if (component && component[RequireAuthentication]) {
      let authenticates = this.configureAutoAuthentication(route);

      this.configureCanActivate(route);

      if (component[SecurityRoles]) {
        console.debug(
          'Secured Route found: [' +
            route.path +
            '] Auto-Authenticates: [' +
            authenticates +
            '] Protected by the following roles: ',
          component[SecurityRoles]
        );
        this.ensureData(route);

        if (route.data) {
          // Needed to prevent a build error, ensure data prevents the route.data from being null
          // We add the roles to the route in the same fashion we configure the route config based settings
          route.data[SecurityRoles] = component[SecurityRoles];
        }
      } else if (component[SecurityGroups]) {
        console.debug(
          'Secured Route found: [' +
            route.path +
            '] Auto-Authenticates: [' +
            authenticates +
            '] Protected by the following groups: ',
          component[SecurityGroups]
        );
        this.ensureData(route);

        if (route.data) {
          // Needed to prevent a build error, ensure data prevents the route.data from being null
          // We add the groups to the route in the same fashion we configure the route config based settings
          route.data[SecurityGroups] = component[SecurityGroups];
        }
      } else {
        console.debug(
          'Secured Route found: [' +
            route.path +
            '] Auto-Authenticates: [' +
            authenticates +
            '] Protected by authentication.'
        );
      }
    }

    if (route.children) {
      for (let child of route.children) {
        this.protectRoutes(child);
      }
    }
  }

  private configureCanActivate(route: Route): void {
    // if we do not have guards then create the collection
    if (!route.canActivate || !Array.isArray(route.canActivate)) {
      route.canActivate = [];
    }

    let containsAuthGuard = false;

    // if we already have the auth guard then do not re-add it
    for (let i = 0; i < route.canActivate.length; i++) {
      if (route.canActivate[i] === AuthGuardService) {
        containsAuthGuard = true;
        break;
      }
    }

    if (!containsAuthGuard) {
      route.canActivate.push(AuthGuardService);
    } else {
      console.warn(
        'The AuthGuardService is added multiple times to the following route: [' +
          route.path +
          '] The route config will be overridden by the annotation settings.'
      );
    }
  }

  private configureAutoAuthentication(route: Route): boolean {
    const component: any = route.component;
    let authenticate = false;

    this.ensureData(route);

    if (component) {
      // Explicitly checking for true to prevent the check for existence
      if (
        component[SecurityAuthenticate] === undefined ||
        component[SecurityAuthenticate] === true
      ) {
        authenticate = true;
      }

      if (route.data) {
        // Needed to prevent a build error, ensure data prevents the route.data from being null
        route.data[SecurityAuthenticate] = authenticate;
      }
    }

    return authenticate;
  }

  private ensureData(route: Route) {
    if (!route.data) {
      route.data = {};
    }
  }

  protected get router(): Router {
    if (this._router === null) {
      this._router = this.injector.get(Router);
    }

    if (this._router === undefined || this._router === null) {
      throw new Error(
        'Null was injected for Router; this is most likely a side effect of other errors. If this is not the case then please notify the library creator.'
      );
    }

    return this._router;
  }
}
